<template>
  <div class="fixed top-4 left-4 bg-white border border-gray-300 rounded-lg p-4 shadow-lg z-50 max-w-md">
    <h3 class="text-lg font-semibold mb-4">Authentication Debug Panel</h3>
    
    <div class="space-y-2 text-sm">
      <div>
        <strong>Frontend Auth Status:</strong>
        <span :class="userStore.isAuthenticated ? 'text-green-600' : 'text-red-600'">
          {{ userStore.isAuthenticated ? 'Authenticated' : 'Not Authenticated' }}
        </span>
      </div>
      
      <div>
        <strong>Token Type:</strong>
        <span :class="tokenType === 'demo' ? 'text-yellow-600' : 'text-blue-600'">
          {{ tokenType }}
        </span>
      </div>
      
      <div>
        <strong>Current User:</strong>
        {{ userStore.currentUser?.name || 'None' }}
      </div>
      
      <div>
        <strong>User Role:</strong>
        {{ userStore.currentUser?.role || 'None' }}
      </div>
    </div>

    <div class="mt-4 space-y-2">
      <button 
        @click="checkBackendAuth"
        class="w-full bg-blue-500 text-white px-3 py-2 rounded text-sm hover:bg-blue-600"
        :disabled="loading"
      >
        {{ loading ? 'Checking...' : 'Check Backend Auth' }}
      </button>
      
      <button 
        @click="testUsersEndpoint"
        class="w-full bg-green-500 text-white px-3 py-2 rounded text-sm hover:bg-green-600"
        :disabled="loading"
      >
        {{ loading ? 'Testing...' : 'Test /api/users' }}
      </button>
      
      <button 
        @click="createAdminUser"
        class="w-full bg-orange-500 text-white px-3 py-2 rounded text-sm hover:bg-orange-600"
        :disabled="loading"
      >
        {{ loading ? 'Creating...' : 'Create Admin User' }}
      </button>
      
      <button 
        @click="loginWithRealCredentials"
        class="w-full bg-purple-500 text-white px-3 py-2 rounded text-sm hover:bg-purple-600"
        :disabled="loading"
      >
        {{ loading ? 'Logging in...' : 'Login with Real Admin' }}
      </button>
      
      <button 
        @click="directLogin"
        class="w-full bg-indigo-500 text-white px-3 py-2 rounded text-sm hover:bg-indigo-600"
        :disabled="loading"
      >
        {{ loading ? 'Logging in...' : 'Direct Login Test' }}
      </button>
      
      <button 
        @click="refreshCsrf"
        class="w-full bg-yellow-500 text-white px-3 py-2 rounded text-sm hover:bg-yellow-600"
        :disabled="loading"
      >
        {{ loading ? 'Refreshing...' : 'Refresh CSRF Token' }}
      </button>
      
      <button 
        @click="testCreateUser"
        class="w-full bg-teal-500 text-white px-3 py-2 rounded text-sm hover:bg-teal-600"
        :disabled="loading"
      >
        {{ loading ? 'Testing...' : 'Test Create User' }}
      </button>
    </div>

    <div v-if="debugInfo" class="mt-4 p-2 bg-gray-100 rounded text-xs overflow-auto max-h-40">
      <pre>{{ JSON.stringify(debugInfo, null, 2) }}</pre>
    </div>

    <div v-if="error" class="mt-2 p-2 bg-red-100 text-red-700 rounded text-xs">
      {{ error }}
    </div>

    <button 
      @click="$emit('close')"
      class="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
    >
      ×
    </button>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useUserStore } from '../stores/userStore'
import axios from '../axios'

defineEmits(['close'])

const userStore = useUserStore()
const loading = ref(false)
const debugInfo = ref(null)
const error = ref(null)

const tokenType = computed(() => {
  const token = localStorage.getItem('token')
  if (!token) return 'none'
  if (token === 'demo_token') return 'demo'
  return 'real'
})

const checkBackendAuth = async () => {
  loading.value = true
  error.value = null
  try {
    const response = await axios.get('/api/auth/debug')
    debugInfo.value = response.data
  } catch (err) {
    error.value = err.response?.data?.message || err.message
    debugInfo.value = {
      error: err.response?.data || err.message,
      status: err.response?.status,
      headers: err.response?.headers
    }
  } finally {
    loading.value = false
  }
}

const testUsersEndpoint = async () => {
  loading.value = true
  error.value = null
  try {
    const response = await axios.get('/api/users')
    debugInfo.value = {
      success: true,
      users_count: response.data.length,
      first_user: response.data[0]
    }
  } catch (err) {
    error.value = err.response?.data?.message || err.message
    debugInfo.value = {
      error: err.response?.data || err.message,
      status: err.response?.status,
      token_in_request: !!axios.defaults.headers.common['Authorization']
    }
  } finally {
    loading.value = false
  }
}

const createAdminUser = async () => {
  loading.value = true
  error.value = null
  try {
    const response = await axios.get('/api/create-admin')
    debugInfo.value = {
      success: true,
      message: response.data.message
    }
  } catch (err) {
    error.value = err.response?.data?.message || err.message
    debugInfo.value = {
      error: err.response?.data || err.message,
      create_failed: true
    }
  } finally {
    loading.value = false
  }
}

const directLogin = async () => {
  loading.value = true
  error.value = null
  try {
    // Get CSRF cookie first
    await axios.get('/sanctum/csrf-cookie')
    
    // Direct login call
    const response = await axios.post('/login', {
      email: 'admin@oddscomposer.io',
      password: 'admin123'
    })
    
    if (response.data.token) {
      localStorage.setItem('token', response.data.token)
      axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`
    }
    
    debugInfo.value = {
      success: true,
      direct_login: true,
      user: response.data.user,
      token: response.data.token ? 'Token received' : 'No token'
    }
  } catch (err) {
    error.value = err.response?.data?.message || err.message
    debugInfo.value = {
      error: err.response?.data || err.message,
      status: err.response?.status,
      direct_login_failed: true
    }
  } finally {
    loading.value = false
  }
}

const refreshCsrf = async () => {
  loading.value = true
  error.value = null
  try {
    const response = await axios.get('/sanctum/csrf-cookie')
    debugInfo.value = {
      success: true,
      csrf_refreshed: true,
      response_status: response.status,
      cookies: document.cookie
    }
  } catch (err) {
    error.value = err.response?.data?.message || err.message
    debugInfo.value = {
      error: err.response?.data || err.message,
      csrf_refresh_failed: true
    }
  } finally {
    loading.value = false
  }
}

const testCreateUser = async () => {
  loading.value = true
  error.value = null
  try {
    // First refresh CSRF
    await axios.get('/sanctum/csrf-cookie')
    
    // Then try to create a test user
    const response = await axios.post('/api/create-user', {
      name: 'Test User',
      email: `test-${Date.now()}@example.com`,
      password: 'testpassword123'
    })
    
    debugInfo.value = {
      success: true,
      user_created: true,
      user: response.data.user
    }
  } catch (err) {
    error.value = err.response?.data?.message || err.message
    debugInfo.value = {
      error: err.response?.data || err.message,
      status: err.response?.status,
      create_user_failed: true,
      headers_sent: axios.defaults.headers.common
    }
  } finally {
    loading.value = false
  }
}

const loginWithRealCredentials = async () => {
  loading.value = true
  error.value = null
  try {
    // Use the actual admin credentials from your database
    const response = await userStore.login({
      email: 'admin@oddscomposer.io', // This matches your database
      password: 'admin123',
      role: 'admin'
    })
    debugInfo.value = {
      success: true,
      user: response,
      token_set: !!localStorage.getItem('token'),
      token_type: localStorage.getItem('token') === 'demo_token' ? 'demo' : 'real'
    }
  } catch (err) {
    error.value = err.message
    debugInfo.value = {
      error: err.message,
      login_failed: true,
      attempted_email: 'admin@oddscomposer.io'
    }
  } finally {
    loading.value = false
  }
}
</script> 